import { Check } from "lucide-react";
import { memo, useState } from "react";
import { FlagBadge } from "@/components/ai/FlagBadge";
import { PhotoFileFace } from "@/components/photos/PhotoFileFace";
import { formatCapturedAt } from "@/lib/format";
import { checkSessionAfterImageError, photoFileUrl } from "@/lib/photo-files";
import type { Photo } from "@/schemas/photos";

/** Memoised, and `onOpen` takes the index, so a new page of rows re-renders only new tiles. */
export const PhotoTile = memo(function PhotoTile({
  photo,
  index,
  selected,
  selecting,
  onOpen,
  onSelect,
}: {
  photo: Photo;
  index: number;
  selected: boolean;
  /** Some photo is selected, so every tile shows its check and a plain click picks. */
  selecting: boolean;
  onOpen: (index: number) => void;
  onSelect: (photoId: string, mode: "toggle" | "range") => void;
}) {
  const [failed, setFailed] = useState(false);
  const showImage = photo.kind === "image" && !failed;

  const pick = (event: { shiftKey: boolean }) =>
    onSelect(photo.id, event.shiftKey ? "range" : "toggle");

  return (
    <div
      data-testid="photo-cell"
      data-selected={selected}
      className={`group relative aspect-square w-full overflow-hidden rounded-md border bg-muted text-muted-foreground ${
        selected ? "ring-2 ring-(--brand-accent) ring-offset-2 ring-offset-background" : ""
      }`}
    >
      <button
        type="button"
        data-testid="photo-tile"
        data-kind={photo.kind}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey) return onSelect(photo.id, "toggle");
          if (event.shiftKey) {
            // Without this the browser also selects the text between the two clicks.
            window.getSelection()?.removeAllRanges();
            return onSelect(photo.id, "range");
          }
          if (selecting) return onSelect(photo.id, "toggle");
          onOpen(index);
        }}
        aria-label={`Open ${photo.fileName}`}
        className="relative block size-full text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {showImage ? (
          <>
            <img
              src={photoFileUrl(photo.id, "thumb")}
              alt=""
              decoding="async"
              onError={() => {
                setFailed(true);
                checkSessionAfterImageError();
              }}
              className="size-full object-cover"
            />
            <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-2 pt-6 pb-1.5 text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              {formatCapturedAt(photo.capturedAt)}
            </span>
          </>
        ) : (
          <PhotoFileFace photo={photo} failed={failed} />
        )}
      </button>
      <button
        type="button"
        aria-pressed={selected}
        aria-label={`Select ${photo.fileName}`}
        data-testid="photo-select"
        onClick={pick}
        className={`absolute top-1.5 left-1.5 flex size-6 items-center justify-center rounded-full pointer-coarse:size-9 border-2 border-white shadow transition-opacity focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
          selected
            ? "bg-(--brand-accent) text-white opacity-100"
            : `bg-black/40 text-transparent ${selecting ? "opacity-100" : "opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100"}`
        }`}
      >
        <Check className="size-3.5" />
      </button>
      {photo.flagCount !== undefined && photo.flagCount > 0 && (
        <FlagBadge
          count={photo.flagCount}
          className="pointer-events-none absolute top-1.5 right-1.5"
        />
      )}
    </div>
  );
});
